import { Box, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { SvgIconComponent } from '@mui/icons-material';
import type { DashboardStatTone } from './DashboardStat';

interface DashboardEmptyStateProps {
    icon: SvgIconComponent;
    title: string;
    description: string;
    tone?: DashboardStatTone;
    /** Actions rendered under the text, e.g. a create button. */
    children?: React.ReactNode;
}

/**
 * Shared empty state for the home cards. The icon sits in a tinted disc at a
 * readable size instead of a 124px washed-out glyph, which used to make a
 * healthy card read as a broken one.
 */
const DashboardEmptyState = ({
    icon: Icon,
    title,
    description,
    tone = 'primary',
    children,
}: DashboardEmptyStateProps) => (
    <Stack flex={1} justifyContent="center" alignItems="center" gap={1.5} textAlign="center" px={2} py={3}>
        <Box
            sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                color: `${tone}.main`,
                bgcolor: (theme) => alpha(theme.palette[tone].main, 0.12),
            }}
        >
            <Icon sx={{ fontSize: 30 }} />
        </Box>
        <Stack gap={0.25} alignItems="center">
            <Typography variant="subtitle1" color="text.primary">
                {title}
            </Typography>
            <Typography variant="body2" color="text.secondary" maxWidth={300}>
                {description}
            </Typography>
        </Stack>
        {children}
    </Stack>
);

export default DashboardEmptyState;
